/* eslint-disable no-console -- We want to log errors */
import type { Data as FavoritesUpdatedEventData } from '@ori-events/favorites-updated';
import { Name as FavoritesUpdatedEventName } from '@ori-events/favorites-updated';
import { Name as RequestFavoritesEventName } from '@ori-events/request-favorites';
import type { Event as ToggleFavoriteEvent } from '@ori-events/toggle-favorite';
import { Name as ToggleFavoriteEventName } from '@ori-events/toggle-favorite';
import type { Data as ToggleFavoriteFailedEventData } from '@ori-events/toggle-favorite-failed';
import { Name as ToggleFavoriteFailedEventName } from '@ori-events/toggle-favorite-failed';
import type { GlobalSharedProductListItemDto } from '@ori/global-api-client/esm/models';

import { client } from './api/Client';
import { Config } from './helpers/Config';
import type { ConfigData } from './helpers/Config';

interface ProductListResponse {
  items: GlobalSharedProductListItemDto[];
  productListId?: string;
}

const PRODUCT_LIST_TYPE = 'Favorite';

const checkFavoritesProductList = async (): Promise<ProductListResponse> => {
  const { customerId, tenant } = Config.get();
  if (customerId !== -1) {
    const { productLists } = await client().getCustomerProductListTypeProductLists(
      tenant,
      customerId,
      PRODUCT_LIST_TYPE,
    );
    const [productList] = productLists;

    if (productList) {
      const { items, productListId } = productList;

      return {
        items,
        productListId,
      };
    }

    const { items, productListId } = await client().createCustomerProductListTypeProductList(
      tenant,
      customerId,
      PRODUCT_LIST_TYPE,
      {
        body: { items: [] },
      },
    );

    return {
      items,
      productListId,
    };
  }
  throw new Error('Not authorized');
};

const setFavorites = async (favorites: string[]) => {
  const { customerId, tenant } = Config.get();

  if (customerId !== -1) {
    const { productListId } = await checkFavoritesProductList();
    await client().updateCustomerProductListTypeProductList(
      tenant,
      customerId,
      PRODUCT_LIST_TYPE,
      String(productListId),
      {
        body: {
          productListId: String(productListId),
          items: favorites.map((favorite) => ({
            itemCode: favorite,
          })),
        },
      },
    );
  }
};

export const render = (rootElement: HTMLElement) => {
  let isLoadingFavorites = false;
  let isReady = false;
  let favorites: string[] = [];

  const { config = '' } = rootElement.dataset;

  if (!config) {
    throw new Error('No config provided!');
  }

  const { customerId, globalApiUrl, tenant } = JSON.parse(config) as ConfigData;

  if (!(customerId && globalApiUrl && tenant)) {
    throw new Error('Missing config values!');
  }

  Config.init({ customerId, tenant, globalApiUrl });

  window.addEventListener(RequestFavoritesEventName, () => {
    if (isReady) {
      window.dispatchEvent(
        new CustomEvent<FavoritesUpdatedEventData>(FavoritesUpdatedEventName, {
          detail: {
            lastUpdated: null,
            favorites,
          },
        }),
      );
    } else if (!isLoadingFavorites) {
      isLoadingFavorites = true;
      checkFavoritesProductList()
        .then((response) => {
          isReady = true;
          isLoadingFavorites = false;
          favorites = response.items
            .map((item) => item.itemCode)
            .filter((code): code is string => code !== undefined);
          window.dispatchEvent(
            new CustomEvent<FavoritesUpdatedEventData>(FavoritesUpdatedEventName, {
              detail: {
                lastUpdated: null,
                favorites,
              },
            }),
          );
        })
        .catch((error) => {
          console.warn(error);
        });
    }
  });
  window.addEventListener(ToggleFavoriteEventName, (({ detail: { code } }: ToggleFavoriteEvent) => {
    if (favorites.includes(code)) {
      favorites = favorites.filter((favorite) => favorite !== code);
    } else {
      favorites.push(code);
    }
    setFavorites(favorites)
      .then(() => {
        window.dispatchEvent(
          new CustomEvent<FavoritesUpdatedEventData>(FavoritesUpdatedEventName, {
            detail: {
              lastUpdated: code,
              favorites,
            },
          }),
        );
      })
      .catch((error) => {
        window.dispatchEvent(
          new CustomEvent<ToggleFavoriteFailedEventData>(ToggleFavoriteFailedEventName, {
            detail: {
              code,
              errorMessage: error instanceof Error ? error.message : 'Unknown error',
            },
          }),
        );
      });
  }) as EventListener);
};
